import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { galleryApi, ApiError } from '../services/api';
import type { GalleryPhoto } from '../types';
import PhotoGrid from '../components/PhotoGrid';
import Lightbox from '../components/Lightbox';
import SkeletonLoader from '../components/SkeletonLoader';

export default function ThemePage() {
  const { year, themeId } = useParams<{ year: string; themeId: string }>();
  const [photos, setPhotos] = useState<GalleryPhoto[] | null>(null);
  const [themeTitle, setThemeTitle] = useState('');
  const [themeDate, setThemeDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const fetchPhotos = () => {
    if (!themeId) return;
    setError(null);
    setPhotos(null);
    setNotFound(false);
    galleryApi
      .getPhotos(themeId)
      .then((res) => {
        setPhotos(res.photos);
        setThemeTitle(res.themeTitle);
        setThemeDate(res.themeDate);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError('Failed to load photos. Please try again.');
        }
      });
  };

  useEffect(() => {
    fetchPhotos();
  }, [themeId]);

  const defaultTitle = themeTitle ? `FWO Photo Gallery - ${themeTitle}` : 'FWO Photo Gallery';
  const defaultDescription = themeTitle
    ? `Browse photos from "${themeTitle}" - FlyWideOpen photo competition`
    : 'Browse FlyWideOpen competition photos';
  const firstPhotoUrl = photos && photos.length > 0 ? photos[0].photoUrl : undefined;

  const helmet = (
    <Helmet>
      <title>{defaultTitle}</title>
      <meta name="description" content={defaultDescription} />
      <meta property="og:site_name" content="FlyWideOpen Photo Gallery" />
      <meta property="og:title" content={defaultTitle} />
      <meta property="og:description" content={defaultDescription} />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:type" content="article" />
      {firstPhotoUrl && <meta property="og:image" content={firstPhotoUrl} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={defaultTitle} />
      <meta name="twitter:description" content={defaultDescription} />
      {firstPhotoUrl && <meta name="twitter:image" content={firstPhotoUrl} />}
    </Helmet>
  );

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        {helmet}
        <p className="text-gray-500 mb-4">Theme not found</p>
        <Link
          to={year ? `/${year}` : '/'}
          className="rounded-md bg-fwo-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          Back to {year ? `${year} Gallery` : 'Gallery'}
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        {helmet}
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchPhotos}
          className="rounded-md bg-fwo-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (photos === null) {
    return (
      <div>
        {helmet}
        <div className="mb-6">
          <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200" />
        </div>
        <SkeletonLoader />
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-center">
        {helmet}
        <p className="text-gray-500">No published photos for this theme</p>
      </div>
    );
  }

  const formattedDate = themeDate
    ? new Date(themeDate + 'T00:00:00').toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div>
      {helmet}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{themeTitle}</h1>
        {formattedDate && (
          <p className="mt-1 text-sm text-gray-500">{formattedDate}</p>
        )}
      </div>
      <PhotoGrid photos={photos} onPhotoClick={(index) => setLightboxIndex(index)} />
      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          currentIndex={lightboxIndex}
          themeTitle={themeTitle}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}
    </div>
  );
}
